import { log2 } from "../../utils/log2";
import { CUSTOM_BLOCKS_API } from "../../api/chatbotCustomBlocks";

const log = log2("CustomBlocksStore");

const customBlocks = {
    namespaced: true,
    state: {
        availableBlocks: [], // All marketplace blocks
        myBlocks: [], // User's created blocks
        importedBlocks: [], // User's imported blocks
        pendingApproval: [], // Blocks pending approval (admin only)
        selectedBlock: null,
    },
    getters: {
        getBlockById: (state) => (id) => {
            return (
                state.availableBlocks.find((x) => x._id === id) ||
                state.myBlocks.find((x) => x._id === id) ||
                state.importedBlocks.find((x) => x.block?._id === id)
            );
        },
        isImported: (state) => (id) => {
            return state.importedBlocks.some((x) => x.blockId === id);
        },
        getImportedBlock: (state) => (id) => {
            return state.importedBlocks.find((x) => x.blockId === id);
        },
    },
    mutations: {
        SET_AVAILABLE_BLOCKS(state, blocks) {
            state.availableBlocks = blocks;
        },
        SET_MY_BLOCKS(state, blocks) {
            state.myBlocks = blocks;
        },
        SET_IMPORTED_BLOCKS(state, blocks) {
            state.importedBlocks = blocks;
        },
        SET_PENDING_APPROVAL(state, blocks) {
            state.pendingApproval = blocks;
        },
        SET_SELECTED_BLOCK(state, block) {
            state.selectedBlock = block;
        },
        ADD_MY_BLOCK(state, block) {
            state.myBlocks.push(block);
        },
        UPDATE_MY_BLOCK(state, block) {
            const index = state.myBlocks.findIndex((x) => x._id === block._id);
            if (index !== -1) {
                state.myBlocks.splice(index, 1, block);
            }
        },
        REMOVE_MY_BLOCK(state, id) {
            state.myBlocks = state.myBlocks.filter((x) => x._id !== id);
        },
        ADD_IMPORTED_BLOCK(state, importRecord) {
            state.importedBlocks.push(importRecord);
        },
        REMOVE_IMPORTED_BLOCK(state, blockId) {
            state.importedBlocks = state.importedBlocks.filter((x) => x.blockId !== blockId);
        },
        UPDATE_IMPORTED_BLOCK(state, importRecord) {
            const index = state.importedBlocks.findIndex((x) => x.blockId === importRecord.blockId);
            if (index !== -1) {
                state.importedBlocks.splice(index, 1, importRecord);
            }
        },
    },
    actions: {
        async listAvailableBlocks({ commit }, filters = {}) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.list(filters);
                commit("SET_AVAILABLE_BLOCKS", data);
                return data;
            } catch (e) {
                log.error("listAvailableBlocks error", e);
                throw e;
            }
        },
        
        async listMyBlocks({ commit }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.getMyBlocks();
                commit("SET_MY_BLOCKS", data);
                return data;
            } catch (e) {
                log.error("listMyBlocks error", e);
                throw e;
            }
        },
        
        async listImportedBlocks({ commit }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.getImported();
                commit("SET_IMPORTED_BLOCKS", data);
                return data;
            } catch (e) {
                log.error("listImportedBlocks error", e);
                throw e;
            }
        },
        
        async listPendingApproval({ commit }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.getPendingApproval();
                commit("SET_PENDING_APPROVAL", data);
                return data;
            } catch (e) {
                log.error("listPendingApproval error", e);
                throw e;
            }
        },
        
        async getBlockDetails({ commit }, { id, includeCode = false }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.getDetails(id, includeCode);
                commit("SET_SELECTED_BLOCK", data);
                return data;
            } catch (e) {
                log.error("getBlockDetails error", e);
                throw e;
            }
        },
        
        async createBlock({ commit, dispatch }, blockData) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.create(blockData);
                commit("ADD_MY_BLOCK", data);
                return data;
            } catch (e) {
                log.error("createBlock error", e);
                throw e;
            }
        },
        
        async updateBlock({ commit }, { id, updates }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.update(id, updates);
                commit("UPDATE_MY_BLOCK", data);
                return data;
            } catch (e) {
                log.error("updateBlock error", e);
                throw e;
            }
        },
        
        async publishBlock({ commit, dispatch }, { id, changelog }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.publish(id, changelog);
                commit("UPDATE_MY_BLOCK", data.block);
                return data;
            } catch (e) {
                log.error("publishBlock error", e);
                throw e;
            }
        },
        
        async unlistBlock({ commit }, id) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.unlist(id);
                commit("UPDATE_MY_BLOCK", data);
                return data;
            } catch (e) {
                log.error("unlistBlock error", e);
                throw e;
            }
        },
        
        async deleteBlock({ commit }, id) {
            try {
                await CUSTOM_BLOCKS_API.delete(id);
                commit("REMOVE_MY_BLOCK", id);
                return true;
            } catch (e) {
                log.error("deleteBlock error", e);
                throw e;
            }
        },
        
        async importBlock({ commit, dispatch }, { id, versionId = null }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.import(id, versionId);
                await dispatch("listImportedBlocks");
                return data;
            } catch (e) {
                log.error("importBlock error", e);
                throw e;
            }
        },
        
        async removeImport({ commit }, id) {
            try {
                await CUSTOM_BLOCKS_API.removeImport(id);
                commit("REMOVE_IMPORTED_BLOCK", id);
                return true;
            } catch (e) {
                log.error("removeImport error", e);
                throw e;
            }
        },
        
        async updateImportConfig({ commit }, { id, config }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.updateImportConfig(id, config);
                commit("UPDATE_IMPORTED_BLOCK", data);
                return data;
            } catch (e) {
                log.error("updateImportConfig error", e);
                throw e;
            }
        },
        
        async rateBlock({ dispatch }, { id, rating, comment }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.rate(id, rating, comment);
                // Refresh block details to get updated rating
                await dispatch("getBlockDetails", { id });
                return data;
            } catch (e) {
                log.error("rateBlock error", e);
                throw e;
            }
        },
        
        async approveBlock({ commit, dispatch }, id) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.approve(id);
                await dispatch("listPendingApproval");
                return data;
            } catch (e) {
                log.error("approveBlock error", e);
                throw e;
            }
        },
        
        async rejectBlock({ commit, dispatch }, { id, reason }) {
            try {
                const { data } = await CUSTOM_BLOCKS_API.reject(id, reason);
                await dispatch("listPendingApproval");
                return data;
            } catch (e) {
                log.error("rejectBlock error", e);
                throw e;
            }
        },
    },
};

export default customBlocks;

